import type { FindItem, RetrieveItem } from "@commercekit/shared";
import classNames from "classnames";
import { useEffect, useRef, useState, type InputHTMLAttributes } from "react";
import { useLoqateContext } from "~/hooks/useLoqate";
import WvField from "./WvField";
import WvInputField from "./WvInputField";
import ArrowIcon from "../icons/ArrowIcon";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  fieldClass?: string;
  placeholder?: string;
  onAddressSelect: (address: RetrieveItem) => void;
  countryCode: string;
  name?: string;
  manualAddressLabel: string;
  addressFinderLabel?: string;
  isManualAddress?: boolean;
  onManualAddressToggle: (val: boolean) => void
}

const WvAddressLookup = ({
  label,
  fieldClass = "col-12",
  placeholder = "Start typing to search address...",
  onAddressSelect,
  countryCode,
  name = "addressLookup",
  manualAddressLabel,
  addressFinderLabel,
  onManualAddressToggle,
  isManualAddress,
}: Props) => {
  const { $pt } = usePrismicLabels();
  const {
    setCountryCode,
    searchTerm,
    updateSearchTerm,
    activeLookupField,
    suggestions,
    isLoading,
    error,
    setSuggestions,
    setActiveContainer,
    retrieveAddress,
  } = useLoqateContext();

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [cachedSuggestions, setCachedSuggestions] = useState<FindItem[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isManualAddress) {
      setShowSuggestions(false);
      setSuggestion(null);
    }
  }, [isManualAddress]);

  // WOV-1840: keep useLoqate's Countries filter in sync with the dropdown
  // so the very first search is country-restricted. Previously the filter was
  // only set on the user's first keystroke and could race the debounced search,
  // letting Belgian results surface for a customer on the .NL site.
  useEffect(() => {
    if (countryCode) {
      setCountryCode(countryCode);
    }
  }, [countryCode, setCountryCode]);

  const handleAddressSelect = async (suggestion: FindItem) => {
    if (suggestion.Type === "Container") {
      setActiveContainer(suggestion.Id);
      setCachedSuggestions(suggestions);
    } else {
      setSuggestion(`${suggestion.Text} ${suggestion.Description || ""}`);
      setCachedSuggestions([]);
      const address = await retrieveAddress(name, suggestion.Id);
      if (address) {
        onAddressSelect(address);
        setShowSuggestions(false);
        updateSearchTerm(name, "");
      }
    }
  };

  const handleBack = () => {
    setSuggestions(cachedSuggestions);
    setCachedSuggestions([]);
  }

  return (
    <div
      ref={wrapperRef}
      className={classNames("position-relative mb-3", fieldClass, {
        "address-lookup": suggestion,
      })}
    >
      {!isManualAddress ? (
        <WvField
          name={name}
          label={suggestion ?? label}
          required={!isManualAddress}
          onClick={() => setShowSuggestions(true)}
          noMarginBottom
        >
          <WvInputField
            name={name}
            value={searchTerm[name] ?? ""}
            onChange={(value) => {
              setCountryCode(countryCode);
              updateSearchTerm(name, value);
              setSuggestion(null);
            }}
            placeholder={suggestion ?? placeholder}
            autoComplete="nope"
            disabled={isManualAddress}
            required={!suggestion && !isManualAddress}
          />
          {name === activeLookupField && isLoading && (
            <div className="position-absolute suggestion-dropdown-loader">
              <div className="spinner"></div>
            </div>
          )}
        </WvField>
      ) : (
        <div
          className="py-2 underline cursor-pointer"
          onClick={
            () => {
              onManualAddressToggle(false)
              setTimeout(() => {
                wrapperRef.current?.querySelector("input")?.focus()
                wrapperRef.current?.querySelector("input")?.click()
              }, 10)
            }
          }
        >
          {addressFinderLabel}
        </div>
      )}

      {showSuggestions && !isLoading && (
        <div
          className="mt-1 bg-white border position-absolute suggestion-dropdown"
          style={{ insetInline: "15px" }}
        >
          {cachedSuggestions.length > 0 && (
            <button
              className="back-btn"
              type="button"
              onClick={handleBack}
            >
              <ArrowIcon /> {$pt("back")}
            </button>
          )}
          <ul className="py-1 suggestion-list">
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.Id}
                className="px-4 py-2 cursor-pointer suggestion-item"
                onClick={() => handleAddressSelect(suggestion)}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span className="font-weight-medium">
                      {suggestion.Text}
                    </span>
                    {suggestion.Type === "Container" && (
                      <span className="fs-200 text-accent">..</span>
                    )}
                    {suggestion.Description && (
                      <span className="ml-2 fs-200 text-muted">
                        {suggestion.Description}
                      </span>
                    )}
                  </div>
                  {suggestion.Type === "Container" && (
                    <span className="fs-200 text-accent">▶</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div
            className="text-center px-4 py-2 underline cursor-pointer"
            onClick={() => onManualAddressToggle(true)}
          >
            {manualAddressLabel}
          </div>
        </div>
      )}

      {error && <div className="mt-1 fs-200 text-danger">{error}</div>}
    </div>
  );
};

export default WvAddressLookup;
